import cn, { Argument as ClassName } from 'classnames'
import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import { pickBy } from 'lodash'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable'

import { SortOrders } from '../../../common/constants/SortOrders'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import chargePointHasPool from '../../../lib/chargePointHasPool'
import { ChargepointStatus } from '../../../lib/chargepoint-status'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import SettingsInfo from '../../../ui/chargepoints/SettingsInfo'
import { RootState } from '../../redux/store'
import { downloadBlob } from '../../services/BlobService'
import {
    ChargePointPageFilterStatus,
    ChargePointsSearchWidget,
} from '../../../common/features/charge-points-search-widget'
import {
    ChargePoint,
    useCompanyGetChargePointsPagedQuery,
    useCompanyRemoveChargePointMutation,
    useLazyCompanyCreateImportStringQuery,
} from '../../../common/redux/api/web-api'
import DropdownMenu, { DropdownMenuItem } from '../../../common/components/DropdownMenu'
import ImportIcon from '../../../lib/icons/import-icon/import-icon'
import DownloadIcon from '../../../lib/icons/download-icon/download-icon'
import { getOwner } from '../../../common/utils/chargePointUtils'
import localizeDate from '../../../common/utils/localizeDate'
import useText from '../../../common/hooks/useText'
import { isFetchBaseQueryError } from '../../../common/redux/api/fetchBaseQueryWithReauth'
import { Table } from '../../../common/uikit/Table'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import { useToast } from '../../../common/uikit/ToastProvider'

import { AddChargePointDialog } from './dialogs/add-charge-point-dialog'
import { ImportChargePointsDialog } from './dialogs/import-charge-points-dialog'

export type AdminChargepointsPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

const mapSearchParamsToFilter = ({
    id,
    imsiNumber,
    companyId,
    status,
    name,
    ownerEmail,
    ownerName,
    ownerMobile,
    ocppOnly,
    productId,
}: Partial<ChargePointPageFilterStatus>): Partial<ChargePointPageFilterStatus> => {
    return {
        id,
        imsiNumber,
        companyId,
        status,
        name,
        ownerEmail,
        ownerName,
        ownerMobile,
        ocppOnly,
        productId,
    }
}

enum SortParameter {
    ID = 'id',
    NAME = 'name',
    STATUS = 'chargePointStatus',
    ORGANISATION = 'companyName',
    LAST_HEARTBEAT = 'lastHeartbeat',
    OWNER_NAME = 'ownerName',
    DESCRIPTION = 'additionalInfo.description',
    LOADBALANCER_GROUP_NAME = 'loadBalancerGroupName',
    POOL = 'poolName',
}

export const AdminChargePointsPage = memo(function AdminChargepointsPage({
    className,
    style,
}: AdminChargepointsPageProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const [chargePointModalOpen, setChargePointModalOpen] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const [importChargePointsDialog, setImportChargePointsDialog] = useState({ visible: false, sessionId: 0 })
    const [pageSize, setPageSize] = useState(20)
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed: boolean }>({
        parameter: SortParameter.ID,
        reversed: false,
    })

    const [searchParams, setParams] = useSearchParams()
    const params = Object.fromEntries(searchParams.entries())
    const filter = mapSearchParamsToFilter(params)

    const { isAdmin, selectedCompanyId: companyId } = useSelector((state: RootState) => state.user)

    const {
        currentData: chargePoints,
        error,
        isFetching: isLoading,
        refetch,
    } = useCompanyGetChargePointsPagedQuery(
        {
            id: filter.id,
            CompanyId: companyId,
            companyId: companyId,
            sortOrder: sortFilter.reversed ? SortOrders.Descending : SortOrders.Ascending,
            column: sortFilter.parameter,
            page: currentPage,
            noOfItems: pageSize,
            status: filter.status,
            productId: filter.productId,
            name: filter.name,
            imsiNumber: filter.imsiNumber,
            ownerName: filter.ownerName,
            ownerEmail: filter.ownerEmail,
            ownerMobile: filter.ownerMobile,
            ocppOnly: filter.ocppOnly,
            expand: 'additionalInfo,ocppConfig',
        },
        { refetchOnMountOrArgChange: true }
    )

    const [removeChargePointMutation] = useCompanyRemoveChargePointMutation()
    const [trigger] = useLazyCompanyCreateImportStringQuery()

    useEffect(() => {
        if (!error) return

        notifyError(
            isFetchBaseQueryError(error) && typeof error.data === 'string'
                ? error.data
                : text('msgChargersFailedToFetch')
        )
    }, [error, text])

    const downloadImportInfo = async () => {
        try {
            notifySuccess(text('msgDownloadImportInformationStarted'))

            const response = await trigger({ companyId: companyId })

            if (!response || !response.data) throw Error

            downloadBlob(response.data, text('lblImportDataCSVFile'))
        } catch (e) {
            notifyError(text('msgDownloadImportInformationFailed'))
        }
    }

    const openAddChargePoint = () => {
        setChargePointModalOpen(true)
    }

    const openImportChargePoint = () => {
        setImportChargePointsDialog(({ sessionId }) => ({ visible: true, sessionId: sessionId + 1 }))
    }

    const hideImportChargePointsDialog = () => {
        setImportChargePointsDialog((dialog) => ({ ...dialog, visible: false }))
    }

    const removeChargePoint = async (chargePoint: ChargePoint) => {
        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgConfirmChargerRemoval'),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removeChargePointMutation({
                companyId: companyId,
                chargePointId: chargePoint.id,
            }).unwrap()

            refetch()
        } catch (e: unknown) {
            notifyError(text('msgChargerFailedToRemove'))
        }
    }

    const saveAndClose = () => {
        refetch()
        setChargePointModalOpen(false)
    }

    const onPage = (event: DataTablePageEvent) => {
        setCurrentPage(event.page ?? event.first / event.rows)
        setPageSize(event.rows)
    }

    const onSort = (event: DataTableSortEvent) => {
        setSortFilter({
            parameter: event.sortField as SortParameter,
            reversed: event.sortOrder === -1,
        })
        setCurrentPage(0)
    }

    const filterSubmit = (filter: Partial<ChargePointPageFilterStatus>) => {
        setParams(pickBy(filter, (value) => value !== undefined && value !== null) as Record<string, string>)
        if (currentPage !== 0) setCurrentPage(0)
    }

    return (
        <>
            <PageLayoutContainer className={cn(className)} style={style}>
                <Card data-testid="table-charge-points" className={cn('flex-1', className)} style={style}>
                    <div className="flex flex-column md:flex-row justify-content-between gap-2">
                        <div className="mr-auto">
                            <Button
                                type="button"
                                label={text('btnAddCharger')}
                                icon="pi pi-plus"
                                onClick={openAddChargePoint}
                                data-testid="open-add-chargepoints-btn"
                            />
                        </div>
                        <div className="flex align-items-center gap-2 w-full md:w-6 md:ml-auto">
                            <div className="flex-1">
                                <ChargePointsSearchWidget onSubmit={filterSubmit} filter={filter} />
                            </div>
                            <DropdownMenu>
                                <DropdownMenuItem
                                    onClick={() => openImportChargePoint()}
                                    text={text('lblChargersImportModal')}
                                    icon={<ImportIcon />}
                                    key="btnImportChargers"
                                    data-testid="open-import-chargepoints-btn"
                                />
                                <DropdownMenuItem
                                    onClick={() => downloadImportInfo()}
                                    text={text('msgDownloadImportInformation')}
                                    icon={<DownloadIcon />}
                                    key="btnDownloadImportInformation"
                                    data-testid="open-download-import-info-btn"
                                />
                            </DropdownMenu>
                        </div>
                    </div>
                    <Table
                        value={chargePoints?.items ?? []}
                        lazy
                        paginator
                        first={currentPage * pageSize}
                        rows={pageSize}
                        totalRecords={chargePoints?.totalCount ?? 0}
                        onPage={onPage}
                        sortField={sortFilter.parameter}
                        sortOrder={sortFilter.reversed ? -1 : 1}
                        onSort={onSort}
                        loading={isLoading}
                        emptyMessage={text('lblNoChargersFound')}
                        className="mt-3"
                        data-testid="chargepoints-table"
                    >
                        <Column
                            field={SortParameter.ID}
                            header={text('lblSerialNumberColumnHeader')}
                            sortable
                            body={(chargePoint: ChargePoint) => (
                                <Link to={`/chargepoints/${chargePoint.id}`}>{chargePoint.id}</Link>
                            )}
                        />
                        <Column
                            field={SortParameter.STATUS}
                            header={text('lblStatus')}
                            sortable
                            body={(chargePoint: ChargePoint) => <ChargepointStatus chargePoint={chargePoint} />}
                        />
                        <Column field={SortParameter.NAME} header={text('lblName')} sortable />
                        <Column
                            field={SortParameter.DESCRIPTION}
                            header={text('lblDescription')}
                            sortable
                            body={(chargePoint: ChargePoint) => chargePoint.additionalInfo?.description}
                            className="hidden-xs hidden-sm"
                        />
                        <Column
                            field={SortParameter.OWNER_NAME}
                            header={text('lblUserOrOwner')}
                            sortable
                            body={(chargePoint: ChargePoint) => getOwner(chargePoint)}
                            className="hidden-xs hidden-sm"
                        />
                        <Column
                            field={SortParameter.LOADBALANCER_GROUP_NAME}
                            header={text('lblLoadBalancerColumnHeader')}
                            sortable
                            body={(chargePoint: ChargePoint) =>
                                chargePoint.loadBalancerGroupId ? (
                                    <Link to={`/loadbalancer/${chargePoint.loadBalancerGroupId}`}>
                                        {chargePoint.loadBalancerGroupName}
                                    </Link>
                                ) : null
                            }
                            className="hidden-xs hidden-sm"
                        />
                        <Column
                            field={SortParameter.POOL}
                            header={text('lblPool')}
                            sortable
                            body={(chargePoint: ChargePoint) =>
                                chargePointHasPool(chargePoint) ? chargePoint.poolName : null
                            }
                            className="hidden-xs hidden-sm"
                        />
                        <Column
                            header={text('lblSettings')}
                            body={(chargePoint: ChargePoint) => <SettingsInfo chargePoint={chargePoint} />}
                        />
                        <Column
                            field={SortParameter.LAST_HEARTBEAT}
                            header={text('lblOfflineSinceColumnHeader')}
                            sortable
                            body={(chargePoint: ChargePoint) =>
                                chargePoint.chargePointStatus === 'Offline'
                                    ? localizeDate(
                                          chargePoint.isOcpp ? chargePoint.offlineSince : chargePoint.lastHeartbeat
                                      )
                                    : null
                            }
                        />
                        <Column
                            body={(chargePoint: ChargePoint) =>
                                isAdmin ? (
                                    <div className="flex justify-content-end gap-2">
                                        <Button
                                            type="button"
                                            icon="pi pi-times"
                                            size="small"
                                            outlined
                                            rounded
                                            severity="danger"
                                            data-testid={`remove-charge-point-${chargePoint.id}`}
                                            aria-label={text('btnRemove')}
                                            tooltip={
                                                chargePoint.loadBalancerGroupId
                                                    ? text('msgRemoveChargerDisabledLoadBalancer')
                                                    : text('btnRemove')
                                            }
                                            tooltipOptions={{ position: 'top', showOnDisabled: true }}
                                            onClick={() => removeChargePoint(chargePoint)}
                                            disabled={!!chargePoint.loadBalancerGroupId}
                                        />
                                    </div>
                                ) : null
                            }
                        />
                    </Table>
                </Card>
            </PageLayoutContainer>
            <AddChargePointDialog
                visible={chargePointModalOpen}
                onSuccess={saveAndClose}
                onHide={() => setChargePointModalOpen(false)}
            />
            <ImportChargePointsDialog
                key={importChargePointsDialog.sessionId}
                visible={importChargePointsDialog.visible}
                onSuccess={() => {
                    hideImportChargePointsDialog()
                    toast.notifySuccess(text('lblChargerWasAdded'))
                    refetch()
                }}
                onHide={hideImportChargePointsDialog}
            />
        </>
    )
})
